/* Main React UI component. Loads the dictionary, creates a SpellingBlitzGame instance,
 manages timer, input/current word, keyboard handlers, feedback, shuffle/submit actions, 
 plays sounds, and calls onScore / onGameEnd. Props: seed, onScore, onGameEnd, timerSeconds, autoStart
*/

import React, { useState, useEffect, useCallback, useRef, useReducer } from 'react';
import SpellingBlitzGame from './SpellingBlitzGame';
import { loadDictionary } from './dictionary';
import settings from '../../core/settings';

function SpellingBlitz({ seed = null, onScore, onGameEnd, timerSeconds = 120, autoStart = false }) {
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  const [currentWord, setCurrentWord] = useState('');
  const [timer, setTimer] = useState(timerSeconds);
  const [timerActive, setTimerActive] = useState(false);
  const [feedback, setFeedback] = useState(null); // { message, type: 'success'|'error'|'pangram' }
  const feedbackTimeout = useRef(null);
  const prevScoreRef = useRef(0);
  const endSentRef = useRef(false);
  const inputRef = useRef(null);
  const hasTypedRef = useRef(false); // once true, placeholder never returns
  const [gridScale, setGridScale] = useState(() => settings.getNumber(settings.KEYS.SB_GRID_SCALE));
  const [showWordList, setShowWordList] = useState(false);

  // Listen for gridScale changes
  useEffect(() => {
    const onCustom = (ev) => {
      try { setGridScale(Number(ev.detail || settings.getNumber(settings.KEYS.SB_GRID_SCALE) || 100)); } catch { setGridScale(100); }
    };
    const onStorage = (e) => {
      if (e.key === settings.KEYS.SB_GRID_SCALE) {
        try { setGridScale(Number(e.newValue || 100)); } catch { setGridScale(100); }
      }
    };
    window.addEventListener('sbGridScaleChanged', onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('sbGridScaleChanged', onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Load dictionary and initialize game
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const dict = await loadDictionary();
      if (cancelled) return;
      const g = new SpellingBlitzGame({ seed, dictionary: dict });
      g.generatePuzzle();
      setGame(g);
      setLoading(false);
      if (autoStart) setTimerActive(true);
    })();
    return () => { cancelled = true; };
  }, [seed, autoStart]);

  // Timer
  useEffect(() => {
    if (!timerActive || timer <= 0) return;
    const id = setInterval(() => setTimer(t => (t > 0 ? t - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [timerActive, timer]);

  // Timer expiry
  useEffect(() => {
    if (timer === 0 && timerActive) {
      setTimerActive(false);
    }
  }, [timer, timerActive]);

  useEffect(() => {
    if (timer === 0 && !endSentRef.current && game) {
      endSentRef.current = true;
      // Play end sound
      (async () => {
        try {
          const master = settings.getNumber(settings.KEYS.MASTER);
          const sfx = settings.getNumber(settings.KEYS.SFX);
          const vol = (master / 100) * (sfx / 100);
          const AudioManager = await import('../../core/AudioManager');
          await AudioManager.initAudio();
          AudioManager.playSound('complete.flac', { volume: vol });
        } catch (e) {}
      })();
      if (onGameEnd) {
        onGameEnd({
          score: game.score,
          foundWords: [...game.foundWords],
          seed: game.seed,
        });
      }
    }
  }, [timer, game, onGameEnd]);

  // Score callback
  useEffect(() => {
    if (!game || !onScore) return;
    if (game.score > prevScoreRef.current) {
      const pts = game.score - prevScoreRef.current;
      onScore(pts);
      prevScoreRef.current = game.score;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.score, onScore]);

  const showFeedback = useCallback((message, type = 'error') => {
    if (feedbackTimeout.current) clearTimeout(feedbackTimeout.current);
    setFeedback({ message, type });
    feedbackTimeout.current = setTimeout(() => setFeedback(null), 1500);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!game || !timerActive || timer === 0) return;
    const word = currentWord.toLowerCase().trim();
    if (!word) return;

    const result = game.submitWord(word);
    setCurrentWord('');
    forceUpdate();

    if (result.valid) {
      const type = result.isPangram ? 'pangram' : 'success';
      showFeedback(`${result.reason} +${result.points}`, type);
      // Play sound
      try {
        const selectedSound = settings.getString(settings.KEYS.SELECTED_SOUND);
        const master = settings.getNumber(settings.KEYS.MASTER);
        const sfx = settings.getNumber(settings.KEYS.SFX);
        if (selectedSound) {
          const vol = (master / 100) * (sfx / 100);
          const AudioManager = await import('../../core/AudioManager');
          await AudioManager.initAudio();
          AudioManager.playSound(selectedSound, { volume: vol });
        }
      } catch (e) {}
    } else {
      showFeedback(result.reason, 'error');
    }
  }, [game, currentWord, timerActive, timer, showFeedback]);

  const handleLetterClick = useCallback((letter) => {
    if (!timerActive || timer === 0) return;
    hasTypedRef.current = true;
    setCurrentWord(prev => prev + letter);
    if (inputRef.current) inputRef.current.focus();
  }, [timerActive, timer]);

  const handleDelete = useCallback(() => {
    setCurrentWord(prev => prev.slice(0, -1));
  }, []);

  const handleShuffle = useCallback(() => {
    if (!game) return;
    game.shuffleOuterLetters();
    forceUpdate();
  }, [game]);

  // Keyboard handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!game || !timerActive || timer === 0) return;
      // Ignore if typing in an unrelated input
      if (e.target.tagName === 'INPUT' && !e.target.classList.contains('sb-word-input')) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        handleDelete();
      } else if (e.key.length === 1 && /^[a-zA-Z]$/.test(e.key)) {
        const letter = e.key.toLowerCase();
        if (game.letters.includes(letter)) {
          e.preventDefault();
          hasTypedRef.current = true;
          setCurrentWord(prev => prev + letter);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [game, timerActive, timer, handleSubmit, handleDelete]);

  if (loading || !game || !game.puzzleReady) {
    return (
      <div className="sb-container">
        <div className="sb-loading">Loading puzzle...</div>
      </div>
    );
  }

  const gameOver = timer === 0;
  const timerPercent = (timer / timerSeconds) * 100;
  const scale = (gridScale || 100) / 100;

  // Build sorted word list: pangrams first, then descending by letter count
  const sortedValidWords = gameOver ? [...game.validWords].sort((a, b) => {
    const aP = game.pangrams.includes(a);
    const bP = game.pangrams.includes(b);
    if (aP && !bP) return -1;
    if (!aP && bP) return 1;
    return b.length - a.length;
  }) : [];

  return (
    <div className={`sb-wrapper ${showWordList ? 'sb-wrapper-with-panel' : ''}`}>
    <div className="sb-container" style={{ transform: `scale(${scale})`, transformOrigin: 'top center' }}>
      {/* Score */}
      <div className="sb-header">
        <span className="sb-score">Score: {game.score}</span>
        <span className="sb-word-count">{game.foundWords.length} / {game.validWords.length} words</span>
      </div>

      {/* Show word list button (after game ends) */}
      {gameOver && (
        <button
          className="sb-btn sb-btn-wordlist"
          onClick={() => setShowWordList(v => !v)}
        >
          {showWordList ? 'Hide Word List' : 'Show Possible Word List'}
        </button>
      )}

      {/* Timer bar */}
      <div className="sb-timer-bar">
        {gameOver ? (
          <span className="sb-timer-text sb-timer-up">Time's Up!</span>
        ) : (
          <span className="sb-timer-text">{timer}s</span>
        )}
        <div className="sb-timer-fill" style={{ width: `${timerPercent}%` }} />
      </div>

      {/* Word input display */}
      <div className="sb-word-display">
        {currentWord ? (
          <span className="sb-word-letters">
            {currentWord.split('').map((ch, i) => (
              <span key={i} className={ch === game.centerLetter ? 'sb-center-char' : ''}>
                {ch.toUpperCase()}
              </span>
            ))}
          </span>
        ) : hasTypedRef.current ? null : (
          <span className="sb-word-placeholder">Type or click letters</span>
        )}
      </div>

      {/* Feedback — always rendered to prevent layout shift */}
      <div className={`sb-feedback ${feedback ? `sb-feedback-${feedback.type}` : 'sb-feedback-hidden'}`}>
        {feedback ? feedback.message : ''}
      </div>

      {/* Honeycomb — 1-2-1-2-1 diamond layout for flat-top hexagons */}
      <div className="sb-honeycomb">
        {/* Row 1: top single */}
        <div className="sb-hex-row">
          <button className="sb-hex sb-hex-outer" onClick={() => handleLetterClick(game.outerLetters[0])} disabled={gameOver}>
            {game.outerLetters[0]?.toUpperCase()}
          </button>
        </div>
        {/* Row 2: upper pair */}
        <div className="sb-hex-row sb-hex-row-pair">
          <button className="sb-hex sb-hex-outer" onClick={() => handleLetterClick(game.outerLetters[1])} disabled={gameOver}>
            {game.outerLetters[1]?.toUpperCase()}
          </button>
          <button className="sb-hex sb-hex-outer" onClick={() => handleLetterClick(game.outerLetters[2])} disabled={gameOver}>
            {game.outerLetters[2]?.toUpperCase()}
          </button>
        </div>
        {/* Row 3: center */}
        <div className="sb-hex-row">
          <button className="sb-hex sb-hex-center" onClick={() => handleLetterClick(game.centerLetter)} disabled={gameOver}>
            {game.centerLetter?.toUpperCase()}
          </button>
        </div>
        {/* Row 4: lower pair */}
        <div className="sb-hex-row sb-hex-row-pair">
          <button className="sb-hex sb-hex-outer" onClick={() => handleLetterClick(game.outerLetters[3])} disabled={gameOver}>
            {game.outerLetters[3]?.toUpperCase()}
          </button>
          <button className="sb-hex sb-hex-outer" onClick={() => handleLetterClick(game.outerLetters[4])} disabled={gameOver}>
            {game.outerLetters[4]?.toUpperCase()}
          </button>
        </div>
        {/* Row 5: bottom single */}
        <div className="sb-hex-row">
          <button className="sb-hex sb-hex-outer" onClick={() => handleLetterClick(game.outerLetters[5])} disabled={gameOver}>
            {game.outerLetters[5]?.toUpperCase()}
          </button>
        </div>
      </div>

      {/* Action buttons */}
      <div className="sb-actions">
        <button className="sb-btn sb-btn-delete" onClick={handleDelete} disabled={gameOver || !currentWord}>
          Delete
        </button>
        <button className="sb-btn sb-btn-shuffle" onClick={handleShuffle} disabled={gameOver}>
          ⟳
        </button>
        <button className="sb-btn sb-btn-enter" onClick={handleSubmit} disabled={gameOver || !currentWord}>
          Enter
        </button>
      </div>

      {/* Found words list */}
      <div className="sb-found-words">
        <div className="sb-found-header">Found Words</div>
        <div className="sb-found-list">
          {game.foundWords.length === 0 ? (
            <span className="sb-found-empty">No words found yet</span>
          ) : (
            game.foundWords.map((w, i) => (
              <span key={i} className={`sb-found-word ${game.pangrams.includes(w) ? 'sb-pangram' : ''}`}>
                {w}
              </span>
            ))
          )}
        </div>
      </div>
    </div>

    {/* Possible word list popup */}
    {showWordList && gameOver && (
      <div className="sb-wordlist-panel">
        <div className="sb-wordlist-header">
          <span>All Valid Words ({game.validWords.length})</span>
          <button className="sb-wordlist-close" onClick={() => setShowWordList(false)}>✕</button>
        </div>
        <div className="sb-wordlist-body">
          {sortedValidWords.map((w, i) => {
            const isPangram = game.pangrams.includes(w);
            const wasFound = game.foundWords.includes(w);
            return (
              <div
                key={i}
                className={`sb-wordlist-item${isPangram ? ' sb-wordlist-pangram' : ''}${wasFound ? ' sb-wordlist-found' : ''}`}
              >
                <span className="sb-wordlist-word">{w}</span>
                <span className="sb-wordlist-meta">
                  {isPangram && <span className="sb-wordlist-badge">★</span>}
                  {wasFound && <span className="sb-wordlist-check">✓</span>}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    )}
    </div>
  );
}

export default SpellingBlitz;
